import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UtensilsCrossed } from "lucide-react";

export default function Login() {
  const { t, lang, setLang } = useI18n();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("welcome"));
      nav("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="hidden md:flex flex-col justify-between p-10 border-r bg-muted/30 relative overflow-hidden">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-display font-bold text-lg">R</div>
          <span className="font-display font-bold text-lg">RestroPilot</span>
        </div>
        <div className="relative z-10">
          <h1 className="font-display font-bold text-4xl leading-tight tracking-tight">
            {t("brandLine1")}<br />
            <span className="text-primary">{t("brandLine2")}</span>
          </h1>
          <p className="mt-4 text-muted-foreground max-w-md">
            {t("brandDesc")}
          </p>
        </div>
        <div className="text-xs text-muted-foreground font-mono">© 2026 RestroPilot · EUR · DD.MM.YYYY</div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="md:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-display font-bold"><UtensilsCrossed className="w-4 h-4" /></div>
            <span className="font-display font-bold">RestroPilot</span>
          </div>

          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display font-bold text-2xl">{t("login")}</h2>
              <p className="text-sm text-muted-foreground mt-1">{t("loginHint")}</p>
            </div>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger className="w-20" data-testid="lang-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">EN</SelectItem>
                <SelectItem value="de">DE</SelectItem>
                <SelectItem value="tr">TR</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t("email")}</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@restaurant.com"
                data-testid="login-email"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">{t("password")}</Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="mt-1"
              />
              <div className="mt-2 text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs text-muted-foreground hover:text-primary hover:underline"
                  data-testid="link-forgot-password"
                >
                  {t("forgotPassword")}
                </Link>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 active:scale-95 transition-transform"
              data-testid="login-submit"
            >
              {loading ? t("loading") : t("login")}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/register" className="text-sm text-primary hover:underline" data-testid="link-register">
              {t("goToRegister")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
